import {
  Box,
  Button,
  Image,
  Input,
  Td,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DeleteModal from "../components/common/Modal/DeleteModal";
import CreateModal from "../components/common/Modal/Modal";
import CreateTable from "../components/common/Table/Table";
import CreateInput from "../components/common/TextInputs/Input";
import Fallback from "../components/common/ui/Fallback";
import Loading from "../components/common/ui/Loading";
import useActions from "../hooks/useActions";
import { useNavigator } from "../hooks/useNavigator";
import {
  createAd,
  deleteAd,
  enableDisableAd,
  getAllAds,
  updateAd,
} from "../utils/api";

const Ads = () => {
  const toast = useToast();
  const nav = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [update, setUpdate] = useState(false);
  const [newAd, setNewAd] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [priceList, setPriceList] = useState([]);
  const [newPriceKey, setNewPriceKey] = useState("");
  const [newPriceValue, setNewPriceValue] = useState("");

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [updateAction, deleteAction] = useActions();

  const fetchAds = async () => {
    setLoading(true);
    const [success, data] = await getAllAds();
    if (success) {
      setAds(data);
    } else {
      toast({
        title: "Error",
        description: "Failed to fetch ads",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
    setLoading(false);
  };

  const [navigate, user] = useNavigator(() => {
    if (user) {
      fetchAds();
    } else {
      navigate("/login");
    }
  });

  const resetForm = () => {
    setNewAd({});
    setImageFile(null);
    setPriceList([]);
    setNewPriceKey("");
    setNewPriceValue("");
    setUpdate(false);
  };

  const handleCreateOrUpdate = async () => {
    if (!newAd.name) {
      toast({
        title: "Error",
        description: "Please fill all required fields",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    const formData = new FormData();
    if (imageFile) {
      formData.append("image", imageFile);
    }
    if (newAd.name) formData.append("name", newAd.name);
    if (newAd.position) formData.append("position", newAd.position);
    if (newAd.expiredDate) formData.append("expiredDate", newAd.expiredDate);

    if (!update) {
      // Create — include priceList and priceMoney
      if (priceList.length > 0) {
        formData.append("priceList", JSON.stringify(priceList));
      }
      if (newAd.priceMoney) formData.append("priceMoney", newAd.priceMoney);

      const [success] = await createAd(formData);
      if (success) {
        toast({
          title: "Event Created",
          description: "Event created successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        fetchAds();
        onClose();
        resetForm();
      } else {
        toast({
          title: "Error",
          description: "Error creating ad",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } else {
      // Update — only image, name, position, expiredDate
      const [success] = await updateAd(formData, newAd._id);
      if (success) {
        toast({
          title: "Event Updated",
          description: "Event updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        fetchAds();
        onClose();
        resetForm();
      } else {
        toast({
          title: "Error",
          description: "Error updating ad",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    await deleteAction(id, deleteAd);
    fetchAds();
    setLoading(false);
  };

  const addPriceListItem = () => {
    if (newPriceKey && newPriceValue) {
      setPriceList((prev) => [
        ...prev,
        { [newPriceKey]: Number(newPriceValue) },
      ]);
      setNewPriceKey("");
      setNewPriceValue("");
    }
  };

  const removePriceListItem = (index) => {
    setPriceList((prev) => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "1rem",
        gap: "1rem",
      }}
    >
      <Button
        colorScheme="blue"
        variant="solid"
        onClick={() => {
          resetForm();
          onOpen();
        }}
      >
        Create Event
      </Button>

      {ads && ads.length > 0 ? (
        <CreateTable
          data={ads}
          searchby={["name"]}
          pagging={true}
          head={[
            "Image",
            "Name",
            "Position",
            "Expired Date",
            "Status",
            "Leaderboard",
            "Actions",
          ]}
          rows={(row) => {
            return (
              <>
                <Td>
                  {row.image ? (
                    <Image
                      src={row.image}
                      alt={row.name}
                      sx={{ width: "80px", height: "80px" }}
                      rounded="md"
                      objectFit="cover"
                    />
                  ) : (
                    "No Image"
                  )}
                </Td>
                <Td>{row.name}</Td>
                <Td>{row.position}</Td>
                <Td>
                  {row.expiredDate
                    ? new Date(row.expiredDate).toLocaleDateString()
                    : "N/A"}
                </Td>
                <Td>
                  <Button
                    colorScheme={row.isEnable ? "red" : "green"}
                    variant="solid"
                    size="sm"
                    onClick={async () => {
                      setLoading(true);
                      await updateAction(
                        { isEnable: !row.isEnable },
                        row._id,
                        enableDisableAd
                      );
                      fetchAds();
                      setLoading(false);
                    }}
                  >
                    {row.isEnable ? "Disable" : "Enable"}
                  </Button>
                </Td>
                <Td>
                  <Button
                    colorScheme="purple"
                    variant="solid"
                    size="sm"
                    onClick={() => nav(`/ads/leaderboard/${row._id}`)}
                  >
                    View Leaderboard
                  </Button>
                </Td>
                <Td>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Button
                      colorScheme="blue"
                      variant="solid"
                      size="sm"
                      onClick={() => {
                        setNewAd(row);
                        setUpdate(true);
                        onOpen();
                      }}
                    >
                      Edit
                    </Button>
                    <DeleteModal onClick={() => handleDelete(row._id)} />
                  </Box>
                </Td>
              </>
            );
          }}
        />
      ) : (
        <Fallback />
      )}

      {/* Create / Edit Modal */}
      <CreateModal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          resetForm();
        }}
        title={update ? "Update Event" : "Create Event"}
        create={handleCreateOrUpdate}
      >
        <CreateInput
          name="Name"
          onChange={(e) =>
            setNewAd((prev) => ({ ...prev, name: e.target.value }))
          }
          value={newAd?.name || ""}
        />

        <Box>
          <Text fontSize="md" fontWeight="semibold" mb={2}>
            Image
          </Text>
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files[0])}
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: "0.375rem",
              padding: "0.5rem",
            }}
          />
          {(imageFile || newAd?.image) && (
            <Text fontSize="sm" color="green.500" mt={1}>
              {imageFile ? `Selected: ${imageFile.name}` : "Current image set"}
            </Text>
          )}
        </Box>

        <CreateInput
          name="Position"
          type="number"
          onChange={(e) =>
            setNewAd((prev) => ({ ...prev, position: e.target.value }))
          }
          value={newAd?.position || ""}
        />

        <Box>
          <Text fontSize="md" fontWeight="semibold" mb={2}>
            Expired Date
          </Text>
          <Input
            type="datetime-local"
            value={
              newAd?.expiredDate
                ? new Date(newAd.expiredDate).toISOString().slice(0, 16)
                : ""
            }
            onChange={(e) =>
              setNewAd((prev) => ({
                ...prev,
                expiredDate: new Date(e.target.value).toISOString(),
              }))
            }
          />
        </Box>

        {/* Only show priceList and priceMoney for creation */}
        {!update && (
          <>
            <CreateInput
              name="Price Money"
              type="number"
              onChange={(e) =>
                setNewAd((prev) => ({
                  ...prev,
                  priceMoney: e.target.value,
                }))
              }
              value={newAd?.priceMoney || ""}
            />

            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={2}>
                Price List
              </Text>
              {priceList.length > 0 && (
                <Box
                  sx={{
                    backgroundColor: "#f7f7f7",
                    padding: "0.75rem",
                    borderRadius: "0.5rem",
                    mb: 2,
                  }}
                >
                  {priceList.map((item, idx) => {
                    const key = Object.keys(item)[0];
                    return (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Text fontSize="sm">
                          Rank {key}: {item[key]}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="red.500"
                          cursor="pointer"
                          onClick={() => removePriceListItem(idx)}
                        >
                          Remove
                        </Text>
                      </Box>
                    );
                  })}
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 2, alignItems: "end" }}>
                <Input
                  placeholder="Rank (e.g. 1)"
                  value={newPriceKey}
                  onChange={(e) => setNewPriceKey(e.target.value)}
                  type="number"
                />
                <Input
                  placeholder="Price (e.g. 400)"
                  value={newPriceValue}
                  onChange={(e) => setNewPriceValue(e.target.value)}
                  type="number"
                />
                <Button
                  colorScheme="green"
                  size="sm"
                  onClick={addPriceListItem}
                  sx={{ minW: "80px" }}
                >
                  Add
                </Button>
              </Box>
            </Box>
          </>
        )}
      </CreateModal>
    </Box>
  );
};

export default Ads;
